'use client';

import Link from 'next/link';
import Image from 'next/image';
import { usePathname, useRouter } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import {
  extractLocaleFromPath,
  buildLocalePath,
  stripTenantPrefix,
  isValidLocale,
  type Locale,
} from '@/i18n/config';
import {
  Calendar,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Menu,
  Search,
  X,
  Instagram,
  Facebook,
  Youtube,
  User,
  LayoutDashboard,
  Heart,
  Download,
  ExternalLink,
  Gift,
  Settings,
  LogOut,
  Trophy,
  MapPin,
  Users,
  Globe,
  Handshake,
  BookOpen,
  HelpCircle,
  Mail,
  FileText,
  Target,
  Sparkles,
  Award,
  ShieldCheck,
  Star,
  Upload,
  Image as ImageIcon,
  ShoppingBag,
  Smartphone,
  Package,
} from 'lucide-react';
import { useState, useEffect, useRef, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { useSearchStore } from '@/stores/search-store';
import { useRegional } from '@/components/providers/regional-provider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select/select';
import { Label } from '@/components/ui/label/label';
import { Flag } from '@/components/ui/flag/flag';
import { useAuth } from '@/hooks/use-auth';
import { useLogout } from '@/hooks/use-logout';
import type { CurrencyOption, PublicUserInfo } from '@impact/types';
import { LANGUAGES } from '@/tenants/_shared/lib/languages';
import { CURRENCY_OPTIONS } from '@/tenants/_shared/lib/currencies';

function toSlug(value: string) {
  return value
    .toLowerCase()
    .replace(/['']/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export interface NavChildItem {
  label: string;
  href: string;
  description?: string;
  icon?: React.ReactNode;
  target?: string;
  rel?: string;
}

export interface NavSection {
  title?: string;
  items: NavChildItem[];
}

export interface NavItem {
  label: string;
  href?: string;
  target?: string;
  rel?: string;
  /** Simple children (flat list) */
  children?: NavChildItem[];
  /** Grouped sections for mega menu style */
  sections?: NavSection[];
  /** Featured item to highlight */
  featured?: {
    title: string;
    description: string;
    href: string;
    image?: string;
  };
}

export interface LocaleOption {
  code: string;
  label: string;
  /** Country code for flag (e.g., 'GB', 'US', 'ES') */
  countryCode: string;
}

// Only include locales that have translation files
const defaultLanguages: LocaleOption[] = LANGUAGES.map((l) => ({
  code: l.code,
  label: l.label,
  countryCode: l.countryCode,
}));

const defaultCurrencies: CurrencyOption[] = CURRENCY_OPTIONS;

export interface PublicHeaderProps {
  tenantName?: string;
  logo?: React.ReactNode;
  navItems?: NavItem[];
  donateHref?: string;
  registerHref?: string;
  loginHref?: string;
  variant?: 'default' | 'transparent';
  /** Use solid background when transparent (useful when content scrolls under header) */
  solidBackground?: boolean;
  /** Available languages for selection */
  languages?: LocaleOption[];
  /** Available currencies for selection */
  currencies?: CurrencyOption[];
  /** Logged in user profile - if provided, overrides internal auth detection */
  user?: PublicUserInfo;
  /** Callback when user logs out */
  onLogout?: () => void;
  /** When false, header participates in layout instead of overlaying page content */
  overlay?: boolean;
}

const getDefaultNavItems = (t: ReturnType<typeof useTranslations>): NavItem[] => [
  {
    label: t('about'),
    sections: [
      {
        title: t('theRide'),
        items: [
          {
            label: t('aboutDgr'),
            href: '/about',
            description: t('aboutDgrDesc'),
            icon: <Target className="w-5 h-5" />,
          },
          {
            label: t('dapperGuide'),
            href: '/about/dapper-guide',
            description: t('dapperGuideDesc'),
            icon: <FileText className="w-5 h-5" />,
          },
          {
            label: t('styleGuide'),
            href: '/about/style-guide',
            description: t('styleGuideDesc'),
            icon: <Calendar className="w-5 h-5" />,
          },
        ],
      },
      {
        title: t('charity'),
        items: [
          {
            label: t('mensHealth'),
            href: '/about/mens-health',
            description: t('mensHealthDesc'),
            icon: <Heart className="w-5 h-5" />,
          },
          {
            label: t('programsWeFund'),
            href: '/about/programs-we-fund',
            description: t('programsWeFundDesc'),
            icon: <Sparkles className="w-5 h-5" />,
          },
        ],
      },
      {
        title: t('partners'),
        items: [
          {
            label: t('triumphMotorcycles'),
            href: '/about/triumphmotorcycles',
            description: t('triumphMotorcyclesDesc'),
            icon: <Star className="w-5 h-5" />,
          },
          {
            label: t('eventPartners'),
            href: '/about/partners',
            description: t('eventPartnersDesc'),
            icon: <Users className="w-5 h-5" />,
          },
          {
            label: t('featuredTeams'),
            href: '/partner-teams',
            description: t('featuredTeamsDesc'),
            icon: <Handshake className="w-5 h-5" />,
          },
        ],
      },
    ],
  },
  {
    label: t('event'),
    sections: [
      {
        title: t('rides'),
        items: [
          {
            label: t('findARide'),
            href: '/events',
            description: t('findARideDesc'),
            icon: <MapPin className="w-5 h-5" />,
          },
          {
            label: t('hostARide'),
            href: '/about/host-a-ride',
            description: t('hostARideDesc'),
            icon: <Users className="w-5 h-5" />,
          },
          {
            label: t('globalMap'),
            href: '/map',
            description: t('globalMapDesc'),
            icon: <Globe className="w-5 h-5" />,
          },
          {
            label: t('sponsorARide'),
            href: '/sponsor',
            description: t('sponsorARideDesc'),
            icon: <Heart className="w-5 h-5" />,
          },
          {
            label: t('dgrApp'),
            href: '/app',
            description: t('dgrAppDesc'),
            icon: <Smartphone className="w-5 h-5" />,
          },
        ],
      },
      {
        title: t('participate'),
        items: [
          {
            label: t('leaderboards'),
            href: '/leaderboard',
            description: t('leaderboardsDesc'),
            icon: <Trophy className="w-5 h-5" />,
          },
          {
            label: t('rewards'),
            href: '/rewards',
            description: t('rewardsDesc'),
            icon: <Award className="w-5 h-5" />,
          },
          {
            label: t('safetyFaq'),
            href: '/about/safety-information',
            description: t('safetyFaqDesc'),
            icon: <ShieldCheck className="w-5 h-5" />,
          },
        ],
      },
    ],
  },
  {
    label: t('support'),
    sections: [
      {
        title: t('helpContact'),
        items: [
          {
            label: t('contactUs'),
            href: '/contact',
            description: t('contactUsDesc'),
            icon: <Mail className="w-5 h-5" />,
          },
          {
            label: t('faqs'),
            href: '/about/faq',
            description: t('faqsDesc'),
            icon: <HelpCircle className="w-5 h-5" />,
          },
          {
            label: t('pressMedia'),
            href: '/press',
            description: t('pressMediaDesc'),
            icon: <FileText className="w-5 h-5" />,
          },
          {
            label: t('downloads'),
            href: '/about/downloads',
            description: t('downloadsDesc'),
            icon: <Download className="w-5 h-5" />,
          },
        ],
      },
      {
        title: t('community'),
        items: [
          {
            label: t('blog'),
            href: '/blog',
            description: t('blogDesc'),
            icon: <BookOpen className="w-5 h-5" />,
          },
          {
            label: t('gallery'),
            href: '/gallery',
            description: t('galleryDesc'),
            icon: <ImageIcon className="w-5 h-5" />,
          },
          {
            label: t('uploadPhotos'),
            href: '/upload',
            description: t('uploadPhotosDesc'),
            icon: <Upload className="w-5 h-5" />,
          },
        ],
      },
    ],
  },
  {
    label: t('shop'),
    href: 'https://shop.gentlemansride.com',
    target: '_blank',
    rel: 'noopener noreferrer',
  },
];

export function PublicHeader({
  logo,
  navItems: navItemsProp,
  donateHref = '/donate',
  registerHref = '/register',
  loginHref = '/login',
  variant = 'default',
  solidBackground = false,
  languages: providedLanguages,
  currencies = defaultCurrencies,
  user: userProp,
  overlay = true,
}: PublicHeaderProps) {
  const rawPathname = usePathname();
  const t = useTranslations('nav');
  const tMy = useTranslations('my');
  const tCommon = useTranslations('common');
  const navItems = navItemsProp ?? getDefaultNavItems(t);
  const { logout, isLoggingOut } = useLogout();
  const { currency: selectedCurrency, setCurrency } = useRegional();
  const router = useRouter();
  const { startSearchTransition } = useSearchStore();

  // Strip /tenants/{slug} prefix from rewritten pathname to get user-facing path
  const publicPathname = useMemo(() => stripTenantPrefix(rawPathname), [rawPathname]);

  // Read locale from NextIntlClientProvider context (set by the server layout
  // from the x-locale header). This is always in sync with the actual content
  // language, unlike URL parsing which desyncs after unprefixed navigation.
  const selectedLanguage = useLocale() as Locale;

  // Build locale-aware href: prepends current locale prefix to a path
  const localePath = useMemo(() => {
    return (href: string) => buildLocalePath(selectedLanguage, href);
  }, [selectedLanguage]);

  // Transform navItems hrefs to include locale prefix
  const localeNavItems = useMemo(() => {
    return navItems.map((item) => ({
      ...item,
      href: item.href && !item.href.startsWith('http') ? localePath(item.href) : item.href,
      children: item.children?.map((child) => ({
        ...child,
        href: localePath(child.href),
      })),
      sections: item.sections?.map((section) => ({
        ...section,
        items: section.items.map((child) => ({
          ...child,
          href: localePath(child.href),
        })),
      })),
      featured: item.featured
        ? { ...item.featured, href: localePath(item.featured.href) }
        : item.featured,
    }));
  }, [navItems, localePath]);

  const {
    user: authUser,
    userType: authUserType,
    authContext,
  } = useAuth({ hydrateFromQuery: true });

  // Build user from best available source
  const user: PublicUserInfo | undefined = useMemo(() => {
    if (userProp) return userProp;

    if (authUser && authUserType) {
      return {
        name: authUser.name,
        email: authUser.email,
        userType: authUserType,
        avatarUrl: authContext?.entrant?.profileImageUrl ?? undefined,
      };
    }

    return undefined;
  }, [userProp, authUser, authUserType, authContext]);

  const eventDetail = useMemo(() => {
    const reg = authContext?.registrationStatus;
    if (!reg?.eventName || !reg.eventSlug || !reg.countryName) return null;
    return {
      name: reg.eventName,
      href: `/events/${toSlug(reg.countryName)}/${reg.eventSlug}`,
    };
  }, [authContext]);

  const entrantUsername = authContext?.entrant?.username ?? null;

  const hasAdminDashboardAccess =
    authContext?.role?.slug === 'owner' ||
    authContext?.role?.slug === 'admin' ||
    authContext?.role?.slug === 'operations_admin';
  const hasEventAdminAccess = Boolean(authContext?.eventAdmin);
  const workspaceLinks = [
    ...(hasAdminDashboardAccess
      ? [
          {
            href: '/dashboard',
            label: t('adminDashboard'),
            buttonLabel: t('dashboard'),
          },
        ]
      : []),
    ...(hasEventAdminAccess
      ? [
          {
            href: '/event-admin',
            label: t('eventAdmin'),
            buttonLabel: t('eventAdmin'),
          },
        ]
      : []),
  ];
  const hasWorkspaceSwitcher = workspaceLinks.length > 1;
  const primaryWorkspaceLink = workspaceLinks[0] ?? null;

  const searchInputRef = useRef<HTMLInputElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSubmenu, setMobileSubmenu] = useState<string | null>(null);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [localeModalOpen, setLocaleModalOpen] = useState(false);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [tempLanguage, setTempLanguage] = useState(selectedLanguage);
  const [tempCurrency, setTempCurrency] = useState(selectedCurrency);
  const [fetchedActiveLanguages, setFetchedActiveLanguages] =
    useState<LocaleOption[]>(defaultLanguages);
  const activeLanguages =
    providedLanguages && providedLanguages.length > 0 ? providedLanguages : fetchedActiveLanguages;

  const isTransparent = variant === 'transparent';
  const usesDarkChrome = isTransparent || !overlay;

  const currentLanguage =
    activeLanguages.find((l) => l.code === selectedLanguage) || activeLanguages[0];
  const currentCurrency = currencies.find((c) => c.code === selectedCurrency) || currencies[0];

  useEffect(() => {
    if (providedLanguages && providedLanguages.length > 0) {
      return;
    }

    let isMounted = true;

    const fetchActiveLanguages = async () => {
      try {
        const response = await fetch('/api/trpc/publicTranslations.getLanguages');
        if (!response.ok) return;

        const json = (await response.json()) as {
          result?: { data?: LocaleOption[] };
        };
        const result = json.result?.data;
        if (!result) return;
        const normalized = result
          .filter((language) => isValidLocale(language.code))
          .map((language) => {
            const lang = language as LocaleOption & Record<string, unknown>;
            return {
              code: String(lang.code).toLowerCase() as Locale,
              label: String(lang.nativeName || lang.name || lang.label || lang.code),
              countryCode: String(lang.flagCode || lang.countryCode || 'GB'),
            };
          });

        if (isMounted && normalized.length > 0) {
          setFetchedActiveLanguages(normalized);
        }
      } catch {
        // Fall back to static languages when API is unavailable.
      }
    };

    void fetchActiveLanguages();

    return () => {
      isMounted = false;
    };
  }, [providedLanguages]);

  // Focus search input when modal opens
  useEffect(() => {
    if (searchModalOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [searchModalOpen]);

  const handleOpenChange = (open: boolean) => {
    setLocaleModalOpen(open);
    if (open) {
      // Reset temp values when opening
      setTempLanguage(selectedLanguage);
      setTempCurrency(selectedCurrency);
    }
  };

  const handleTempLanguageChange = (lang: Locale) => {
    setTempLanguage(lang);
  };

  const handleSave = () => {
    // Always persist locale cookie — covers the case where the URL shows the default
    // locale (no prefix) but the cookie still holds a previous non-default locale,
    // causing the proxy to serve stale translations.
    document.cookie = `NEXT_LOCALE=${tempLanguage}; path=/; max-age=31536000`;

    // Persist currency cookie before navigation so the reload picks it up
    if (tempCurrency !== selectedCurrency) {
      setCurrency(tempCurrency);
    }

    // Always hard-navigate to force the server layout to re-execute with the
    // correct locale. Even when the URL path hasn't changed (e.g. selecting English
    // while already on an unprefixed URL), the cookie may have been stale.
    const [, pathWithoutLocale] = extractLocaleFromPath(publicPathname);
    const newPath = buildLocalePath(tempLanguage, pathWithoutLocale);
    window.location.href = newPath + window.location.search;
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = searchQuery.trim();
    if (trimmed) {
      startSearchTransition(trimmed);
      setSearchModalOpen(false);
      setSearchQuery('');
      router.push(`${localePath('/search')}?q=${encodeURIComponent(trimmed)}`);
    }
  };

  // Format display string for the locale button (flag + currency)
  const localeDisplay = currentCurrency?.code || 'USD';

  return (
    <>
      <header
        className={cn(
          overlay ? 'absolute top-0 left-0 right-0 z-50 w-full' : 'relative z-50 w-full',
          isTransparent && !solidBackground && 'bg-transparent',
          isTransparent && solidBackground && 'bg-stone-950/70 backdrop-blur-sm',
          !isTransparent && !overlay && 'bg-stone-950',
          !isTransparent && overlay && 'bg-stone-950/40 backdrop-blur-sm',
        )}
      >
        <div className="container flex h-20 items-center justify-between">
          {/* Left side: Logo + Navigation */}
          <div className="flex items-center gap-3 md:gap-4 lg:gap-8">
            {/* Logo and Brand */}
            <Link prefetch={false} href={localePath('/')} className="flex items-center gap-3">
              {logo || (
                <Image
                  src="/images/dgr/DGRLogo.svg"
                  alt={tCommon('brandAlt')}
                  width={140}
                  height={59}
                  className="h-8 lg:h-12 w-auto"
                />
              )}
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {localeNavItems.map((item) =>
                item.sections ? (
                  // Mega menu style dropdown with sections
                  <DropdownMenu key={item.label}>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className={cn(
                          'gap-1',
                          usesDarkChrome && 'text-white hover:bg-white/10 hover:text-white',
                        )}
                      >
                        {item.label}
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="start"
                      className={cn(
                        'p-0 border-0 shadow-xl',
                        item.featured
                          ? 'w-[700px]'
                          : item.sections.length >= 3
                            ? 'w-[680px]'
                            : 'w-[480px]',
                      )}
                    >
                      <div className="flex flex-col">
                        <div className="flex">
                          {/* Sections */}
                          <div
                            className={cn(
                              'flex-1 grid gap-0 p-4',
                              item.sections.length >= 3
                                ? 'grid-cols-3'
                                : item.sections.length > 1
                                  ? 'grid-cols-2'
                                  : 'grid-cols-1',
                            )}
                          >
                            {item.sections.map((section, sectionIdx) => (
                              <div key={section.title || sectionIdx} className="space-y-1">
                                {section.title && (
                                  <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                    {section.title}
                                  </div>
                                )}
                                {section.items.map((child) => (
                                  <Link
                                    prefetch={false}
                                    key={child.href}
                                    href={child.href}
                                    target={child.target}
                                    rel={child.rel}
                                    className="flex items-start gap-3 px-3 py-2.5 rounded-lg hover:bg-accent transition-colors group"
                                  >
                                    {child.icon && (
                                      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-muted flex items-center justify-center text-muted-foreground group-hover:bg-dgr-gold/10 group-hover:text-dgr-gold transition-colors">
                                        {child.icon}
                                      </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium text-sm">{child.label}</div>
                                      {child.description && (
                                        <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                                          {child.description}
                                        </div>
                                      )}
                                    </div>
                                  </Link>
                                ))}
                              </div>
                            ))}
                          </div>
                          {/* Featured section */}
                          {item.featured && (
                            <div className="w-[200px] bg-muted/50 p-4 border-l">
                              <Link
                                prefetch={false}
                                href={item.featured.href}
                                className="block group"
                              >
                                {item.featured.image && (
                                  <div className="relative w-full aspect-[4/3] rounded-lg overflow-hidden mb-3">
                                    {/* eslint-disable-next-line @next/next/no-img-element */}
                                    <img
                                      src={item.featured.image}
                                      alt={item.featured.title}
                                      className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-300"
                                    />
                                  </div>
                                )}
                                <div className="font-semibold text-sm group-hover:text-dgr-gold transition-colors">
                                  {item.featured.title}
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">
                                  {item.featured.description}
                                </div>
                                <div className="flex items-center gap-1 text-xs font-medium text-dgr-gold mt-2">
                                  {tCommon('learnMore')}
                                  <ChevronRight className="w-3 h-3" />
                                </div>
                              </Link>
                            </div>
                          )}
                        </div>
                        {/* Social links footer - only for Support menu */}
                        {item.label === t('support') && (
                          <div className="border-t bg-muted/30 px-4 py-3">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-muted-foreground">
                                {t('followUs')}
                              </span>
                              <div className="flex items-center gap-3">
                                <a
                                  href="https://instagram.com/gentlemansride"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-muted-foreground hover:text-dgr-gold transition-colors"
                                >
                                  <Instagram className="w-6 h-6" />
                                </a>
                                <a
                                  href="https://facebook.com/gentlemansride"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-muted-foreground hover:text-dgr-gold transition-colors"
                                >
                                  <Facebook className="w-6 h-6" />
                                </a>
                                <a
                                  href="https://youtube.com/@DistinguishedGentlemansRide"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-muted-foreground hover:text-dgr-gold transition-colors"
                                >
                                  <Youtube className="w-6 h-6" />
                                </a>
                                <a
                                  href="https://x.com/gentlemansride"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-muted-foreground hover:text-dgr-gold transition-colors"
                                >
                                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                  </svg>
                                </a>
                                <a
                                  href="https://www.tiktok.com/@gentlemansride"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-muted-foreground hover:text-dgr-gold transition-colors"
                                >
                                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V9.4a8.16 8.16 0 0 0 4.77 1.52V7.47a4.85 4.85 0 0 1-1.01-.78z" />
                                  </svg>
                                </a>
                                <a
                                  href="https://www.linkedin.com/company/gentlemansride"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-muted-foreground hover:text-dgr-gold transition-colors"
                                >
                                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                                  </svg>
                                </a>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : item.children ? (
                  // Simple dropdown with icons
                  <DropdownMenu key={item.label}>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className={cn(
                          'gap-1',
                          usesDarkChrome && 'text-white hover:bg-white/10 hover:text-white',
                        )}
                      >
                        {item.label}
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-[280px] p-2">
                      {item.children.map((child) => (
                        <Link
                          prefetch={false}
                          key={child.href}
                          href={child.href}
                          target={child.target}
                          rel={child.rel}
                          className="flex items-start gap-3 px-3 py-2.5 rounded-lg hover:bg-accent transition-colors group"
                        >
                          {child.icon && (
                            <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-muted flex items-center justify-center text-muted-foreground group-hover:bg-dgr-gold/10 group-hover:text-dgr-gold transition-colors">
                              {child.icon}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm">{child.label}</div>
                            {child.description && (
                              <div className="text-xs text-muted-foreground mt-0.5">
                                {child.description}
                              </div>
                            )}
                          </div>
                        </Link>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Button
                    key={item.label}
                    variant="ghost"
                    asChild
                    className={cn(
                      usesDarkChrome && 'text-white hover:bg-white/10 hover:text-white',
                    )}
                  >
                    <Link prefetch={false} href={item.href!} target={item.target} rel={item.rel}>
                      {item.label}
                    </Link>
                  </Button>
                ),
              )}
            </nav>
          </div>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-3">
            {/* Search Button */}
            <Dialog open={searchModalOpen} onOpenChange={setSearchModalOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(usesDarkChrome && 'text-white hover:bg-white/10 hover:text-white')}
                >
                  <Search className="h-4 w-4" />
                  <span className="sr-only">{t('search')}</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-2xl top-[15%] translate-y-0">
                <DialogHeader>
                  <DialogTitle className="text-xl">{t('search')}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSearch} className="mt-2">
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      ref={searchInputRef}
                      type="text"
                      placeholder={t('searchPlaceholder')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="h-14 pl-12 pr-4 text-lg bg-muted/50 border-border focus-visible:ring-dgr-gold"
                    />
                  </div>
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-sm text-muted-foreground">{t('pressEnterToSearch')}</p>
                    <Button
                      type="submit"
                      disabled={!searchQuery.trim()}
                      className="bg-dgr-gold hover:bg-dgr-gold-hover text-white"
                    >
                      {t('search')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            {/* Locale Selector */}
            <Dialog open={localeModalOpen} onOpenChange={handleOpenChange}>
              <DialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    'gap-2 text-sm',
                    usesDarkChrome && 'text-white hover:bg-white/10 hover:text-white',
                  )}
                >
                  <Flag code={currentLanguage?.countryCode || 'GB'} size="sm" />
                  <span className="hidden xl:inline">/ {localeDisplay}</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>{t('regionLanguage')}</DialogTitle>
                </DialogHeader>
                <div className="grid gap-5 py-4">
                  <div className="grid gap-2">
                    <Label htmlFor="language">{t('language')}</Label>
                    <Select
                      value={tempLanguage}
                      onValueChange={(v) => handleTempLanguageChange(v as Locale)}
                    >
                      <SelectTrigger id="language">
                        <SelectValue placeholder={t('selectLanguage')} />
                      </SelectTrigger>
                      <SelectContent>
                        {activeLanguages.map((lang) => (
                          <SelectItem key={lang.code} value={lang.code}>
                            <span className="flex items-center gap-2">
                              <Flag code={lang.countryCode} size="sm" />
                              <span>{lang.label}</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="currency">{t('currency')}</Label>
                    <Select value={tempCurrency} onValueChange={setTempCurrency}>
                      <SelectTrigger id="currency">
                        <SelectValue placeholder={t('selectCurrency')} />
                      </SelectTrigger>
                      <SelectContent>
                        {currencies.map((curr) => (
                          <SelectItem key={curr.code} value={curr.code}>
                            <span className="flex items-center gap-2">
                              <span className="w-8 font-mono text-muted-foreground">
                                {curr.symbol}
                              </span>
                              <span>{curr.label}</span>
                              <span className="text-muted-foreground">({curr.code})</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setLocaleModalOpen(false)}>
                    {tCommon('cancel')}
                  </Button>
                  <Button onClick={handleSave}>{tCommon('save')}</Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Auth Actions - Login/Register or User Menu */}
            {user ? (
              <>
                {(hasWorkspaceSwitcher || primaryWorkspaceLink) &&
                  (hasWorkspaceSwitcher ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          className={cn(usesDarkChrome && 'text-white hover:bg-white/10')}
                        >
                          <LayoutDashboard className="h-4 w-4 mr-2" />
                          {t('dashboard')}
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        {workspaceLinks.map((workspace) => (
                          <DropdownMenuItem key={workspace.href} asChild>
                            <Link prefetch={false} href={workspace.href} className="cursor-pointer">
                              <LayoutDashboard className="h-4 w-4 mr-2" />
                              {workspace.label}
                            </Link>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    <Button
                      variant="ghost"
                      asChild
                      className={cn(usesDarkChrome && 'text-white hover:bg-white/10')}
                    >
                      <Link prefetch={false} href={primaryWorkspaceLink.href}>
                        <LayoutDashboard className="h-4 w-4 mr-2" />
                        {primaryWorkspaceLink.buttonLabel}
                      </Link>
                    </Button>
                  ))}

                {/* User Avatar Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className={cn(
                        'flex items-center gap-2 rounded-full pr-3 transition-colors border-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dgr-gold/50',
                        'border-dgr-gold hover:border-dgr-gold-light data-[state=open]:border-dgr-gold',
                        'bg-black/50 backdrop-blur-sm hover:bg-black/60',
                      )}
                    >
                      <span className="flex-shrink-0 w-9 h-9 rounded-full overflow-hidden">
                        {user.avatarUrl ? (
                          /* eslint-disable-next-line @next/next/no-img-element */
                          <img
                            src={user.avatarUrl}
                            alt={user.name}
                            className="w-full h-full rounded-full object-cover"
                          />
                        ) : (
                          <span className="w-full h-full flex items-center justify-center text-sm font-medium bg-white text-stone-950">
                            {user.name.charAt(0).toUpperCase()}
                          </span>
                        )}
                      </span>
                      <span className={cn('text-sm font-medium max-w-[100px] truncate text-white')}>
                        {user.name.split(' ')[0]}
                      </span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-3 py-2 border-b">
                      <p className="font-medium truncate">{user.name}</p>
                      {user.email && (
                        <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                      )}
                    </div>
                    <DropdownMenuItem asChild>
                      <Link prefetch={false} href="/my/dashboard" className="cursor-pointer">
                        <LayoutDashboard className="h-4 w-4 mr-2" />
                        {t('myDashboard')}
                      </Link>
                    </DropdownMenuItem>
                    {entrantUsername && (
                      <DropdownMenuItem asChild>
                        <Link
                          prefetch={false}
                          href={`/fundraiser/${entrantUsername}`}
                          className="cursor-pointer"
                        >
                          <User className="h-4 w-4 mr-2" />
                          {tMy('viewPublicProfile')}
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem asChild>
                      <Link prefetch={false} href="/my/events" className="cursor-pointer">
                        <CalendarDays className="h-4 w-4 mr-2" />
                        {t('myEvents')}
                      </Link>
                    </DropdownMenuItem>
                    {eventDetail && (
                      <DropdownMenuItem asChild>
                        <Link prefetch={false} href={eventDetail.href} className="cursor-pointer">
                          <MapPin className="h-4 w-4 mr-2" />
                          {t('eventDetails', { eventName: eventDetail.name })}
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem asChild>
                      <Link prefetch={false} href="/my/donations" className="cursor-pointer">
                        <Heart className="h-4 w-4 mr-2" />
                        {t('myDonations')}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link prefetch={false} href="/my/orders" className="cursor-pointer">
                        <Package className="h-4 w-4 mr-2" />
                        {t('myOrders')}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link prefetch={false} href="/my/downloads" className="cursor-pointer">
                        <Download className="h-4 w-4 mr-2" />
                        {t('downloads')}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link prefetch={false} href="/my/offers" className="cursor-pointer">
                        <Gift className="h-4 w-4 mr-2" />
                        {t('exclusiveOffers')}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link prefetch={false} href="/my/team/dashboard" className="cursor-pointer">
                        <Users className="h-4 w-4 mr-2" />
                        {t('myTeam')}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link prefetch={false} href="/my/settings" className="cursor-pointer">
                        <Settings className="h-4 w-4 mr-2" />
                        {t('settings')}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => void logout()}
                      disabled={isLoggingOut}
                      className="cursor-pointer"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      {isLoggingOut ? t('signingOut') : t('logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Button
                  variant={usesDarkChrome ? 'ghost' : 'outline'}
                  asChild
                  className={cn(
                    usesDarkChrome &&
                      'border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white',
                  )}
                >
                  <Link prefetch={false} href={loginHref}>
                    {t('login')}
                  </Link>
                </Button>
                <Button
                  variant="outline"
                  asChild
                  className={cn(
                    usesDarkChrome &&
                      'border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white',
                  )}
                >
                  <Link prefetch={false} href={registerHref}>
                    {t('register')}
                  </Link>
                </Button>
              </>
            )}

            {/* Donate button always visible */}
            <Button
              asChild
              className={cn(usesDarkChrome && 'bg-dgr-gold text-stone-950 hover:bg-dgr-gold-light')}
            >
              <Link prefetch={false} href={donateHref}>
                {t('donate')}
              </Link>
            </Button>
          </div>

          {/* Mobile Buttons */}
          <div className="lg:hidden flex items-center gap-2">
            {/* User Profile Button - Only shown when logged in */}
            {user && (
              <button
                onClick={() => {
                  setAccountMenuOpen(!accountMenuOpen);
                  if (accountMenuOpen) setMobileMenuOpen(false);
                }}
                className={cn(
                  'flex items-center gap-2 rounded-full pr-3 transition-colors border-2',
                  'bg-black/50 backdrop-blur-sm hover:bg-black/60',
                  accountMenuOpen
                    ? 'border-dgr-gold'
                    : usesDarkChrome
                      ? 'border-dgr-gold hover:border-dgr-gold-light'
                      : 'border-dgr-gold hover:border-dgr-gold-light',
                )}
              >
                <span className="flex-shrink-0 w-9 h-9 rounded-full overflow-hidden">
                  {user.avatarUrl ? (
                    /* eslint-disable-next-line @next/next/no-img-element */
                    <img
                      src={user.avatarUrl}
                      alt={user.name}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <span className="w-full h-full flex items-center justify-center text-sm font-medium bg-white text-stone-950">
                      {user.name.charAt(0).toUpperCase()}
                    </span>
                  )}
                </span>
                <span className={cn('text-sm font-medium max-w-[100px] truncate text-white')}>
                  {user.name.split(' ')[0]}
                </span>
              </button>
            )}

            {/* Login Button - Only shown when not logged in */}
            {!user && (
              <Button
                variant="ghost"
                size="sm"
                asChild
                className={cn(usesDarkChrome && 'text-white hover:bg-white/10')}
              >
                <Link prefetch={false} href={loginHref}>
                  {t('login')}
                </Link>
              </Button>
            )}

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className={cn(usesDarkChrome && 'text-white hover:bg-white/10')}
              onClick={() => {
                setMobileMenuOpen(!mobileMenuOpen);
                if (mobileMenuOpen) setMobileSubmenu(null);
                if (!mobileMenuOpen) setAccountMenuOpen(false);
              }}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile Navigation - Full Screen Overlay */}
      <div
        className={cn(
          'lg:hidden fixed inset-0 z-50 flex flex-col transition-all duration-300 ease-out',
          mobileMenuOpen
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 translate-y-2 pointer-events-none',
          usesDarkChrome ? 'bg-stone-950/95 backdrop-blur-md' : 'bg-background/95 backdrop-blur-md',
        )}
      >
        {/* Fixed Header with Logo and Close */}
        <div className="flex-shrink-0 h-20 flex items-center justify-between px-6">
          <Link
            prefetch={false}
            href={localePath('/')}
            onClick={() => {
              setMobileMenuOpen(false);
              setMobileSubmenu(null);
            }}
            className="flex items-center gap-3"
          >
            {logo || (
              <Image
                src="/images/dgr/DGRLogo.svg"
                alt={tCommon('brandAlt')}
                width={140}
                height={59}
                className="h-12 w-auto"
              />
            )}
          </Link>
          <Button
            variant="ghost"
            size="icon"
            className={cn(usesDarkChrome && 'text-white hover:bg-white/10')}
            onClick={() => {
              setMobileMenuOpen(false);
              setMobileSubmenu(null);
            }}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Scrollable Content Area - Only this part slides */}
        <div className="flex-1 overflow-hidden relative">
          {/* Main Menu Content */}
          <div
            className={cn(
              'absolute inset-0 overflow-y-auto transition-transform duration-300 ease-out px-6 py-4',
              mobileSubmenu ? '-translate-x-full' : 'translate-x-0',
            )}
          >
            <div className="space-y-0.5">
              {localeNavItems.map((item) =>
                item.children || item.sections ? (
                  <button
                    key={item.label}
                    onClick={() => setMobileSubmenu(item.label)}
                    className={cn(
                      'w-full flex items-center justify-between py-2 text-lg font-medium border-b transition-colors',
                      usesDarkChrome
                        ? 'text-white border-white/10 hover:text-dgr-gold'
                        : 'text-foreground border-border hover:text-primary',
                    )}
                  >
                    <span>{item.label}</span>
                    <ChevronRight className="w-5 h-5 opacity-50" />
                  </button>
                ) : (
                  <Link
                    prefetch={false}
                    key={item.label}
                    href={item.href!}
                    target={item.target}
                    rel={item.rel}
                    className={cn(
                      'block py-2 text-lg font-medium border-b transition-colors',
                      usesDarkChrome
                        ? 'text-white border-white/10 hover:text-dgr-gold'
                        : 'text-foreground border-border hover:text-primary',
                    )}
                    onClick={() => {
                      setMobileMenuOpen(false);
                      setMobileSubmenu(null);
                    }}
                  >
                    {item.label}
                  </Link>
                ),
              )}
            </div>

            {/* Secondary Actions */}
            <div className="mt-8 space-y-3">
              {/* Login link - only shown when not logged in */}
              {!user && (
                <Link
                  prefetch={false}
                  href={loginHref}
                  onClick={() => {
                    setMobileMenuOpen(false);
                    setMobileSubmenu(null);
                  }}
                  className={cn(
                    'flex items-center gap-3 py-3 text-base transition-colors',
                    usesDarkChrome
                      ? 'text-white/70 hover:text-white'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  <User className="w-5 h-5" />
                  <span>{t('login')}</span>
                </Link>
              )}
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  setMobileSubmenu(null);
                  setSearchModalOpen(true);
                }}
                className={cn(
                  'flex items-center gap-3 py-3 text-base transition-colors',
                  usesDarkChrome
                    ? 'text-white/70 hover:text-white'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <Search className="w-5 h-5" />
                <span>{t('search')}</span>
              </button>
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  setMobileSubmenu(null);
                  setLocaleModalOpen(true);
                }}
                className={cn(
                  'flex items-center gap-3 py-3 text-base transition-colors',
                  usesDarkChrome
                    ? 'text-white/70 hover:text-white'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <Flag code={currentLanguage?.countryCode || 'GB'} size="sm" />
                <span>/ {localeDisplay}</span>
              </button>
            </div>
          </div>

          {/* Submenu Content */}
          <div
            className={cn(
              'absolute inset-0 overflow-y-auto transition-transform duration-300 ease-out px-6 py-4',
              mobileSubmenu ? 'translate-x-0' : 'translate-x-full',
            )}
          >
            {/* Back Button */}
            <button
              onClick={() => setMobileSubmenu(null)}
              className={cn(
                'flex items-center gap-2 mb-6 text-sm font-medium transition-colors',
                usesDarkChrome
                  ? 'text-white/70 hover:text-white'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <ChevronLeft className="w-4 h-4" />
              <span>{tCommon('back')}</span>
            </button>

            {/* Submenu Title */}
            <h2
              className={cn(
                'text-2xl font-bold mb-6',
                usesDarkChrome ? 'text-white' : 'text-foreground',
              )}
            >
              {mobileSubmenu}
            </h2>

            {/* Submenu Items */}
            <div className="space-y-1">
              {(() => {
                const activeItem = localeNavItems.find((item) => item.label === mobileSubmenu);
                // Get all items from either children or sections
                const allItems =
                  activeItem?.children ||
                  activeItem?.sections?.flatMap((section) => section.items) ||
                  [];

                return allItems.map((child) => (
                  <Link
                    prefetch={false}
                    key={child.href}
                    href={child.href}
                    target={child.target}
                    rel={child.rel}
                    className={cn(
                      'flex items-center gap-3 py-2 text-lg border-b transition-colors',
                      usesDarkChrome
                        ? 'text-white border-white/10 hover:text-dgr-gold'
                        : 'text-foreground border-border hover:text-primary',
                    )}
                    onClick={() => {
                      setMobileMenuOpen(false);
                      setMobileSubmenu(null);
                    }}
                  >
                    {child.icon && <span className="opacity-60">{child.icon}</span>}
                    <span>{child.label}</span>
                  </Link>
                ));
              })()}
            </div>
          </div>
        </div>

        {/* Fixed Footer - Social Links & CTAs */}
        <div
          className={cn(
            'flex-shrink-0 px-6 py-5 border-t',
            usesDarkChrome ? 'border-white/10' : 'border-border',
          )}
        >
          {/* Social Media Links */}
          <div className="flex items-center justify-center gap-6 mb-5">
            <a
              href="https://instagram.com/gentlemansride"
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                'p-2 rounded-full transition-colors',
                usesDarkChrome
                  ? 'text-white/60 hover:text-white hover:bg-white/10'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent',
              )}
              onClick={() => {
                setMobileMenuOpen(false);
                setMobileSubmenu(null);
              }}
            >
              <Instagram className="w-5 h-5" />
              <span className="sr-only">Instagram</span>
            </a>
            <a
              href="https://facebook.com/gentlemansride"
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                'p-2 rounded-full transition-colors',
                usesDarkChrome
                  ? 'text-white/60 hover:text-white hover:bg-white/10'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent',
              )}
              onClick={() => {
                setMobileMenuOpen(false);
                setMobileSubmenu(null);
              }}
            >
              <Facebook className="w-5 h-5" />
              <span className="sr-only">Facebook</span>
            </a>
            <a
              href="https://youtube.com/gentlemansride"
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                'p-2 rounded-full transition-colors',
                usesDarkChrome
                  ? 'text-white/60 hover:text-white hover:bg-white/10'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent',
              )}
              onClick={() => {
                setMobileMenuOpen(false);
                setMobileSubmenu(null);
              }}
            >
              <Youtube className="w-5 h-5" />
              <span className="sr-only">YouTube</span>
            </a>
            <a
              href="https://x.com/gentlemansride"
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                'p-2 rounded-full transition-colors',
                usesDarkChrome
                  ? 'text-white/60 hover:text-white hover:bg-white/10'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent',
              )}
              onClick={() => {
                setMobileMenuOpen(false);
                setMobileSubmenu(null);
              }}
            >
              {/* X (Twitter) icon */}
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
              <span className="sr-only">X (Twitter)</span>
            </a>
            <a
              href="https://www.tiktok.com/@gentlemansride"
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                'p-2 rounded-full transition-colors',
                usesDarkChrome
                  ? 'text-white/60 hover:text-white hover:bg-white/10'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent',
              )}
              onClick={() => {
                setMobileMenuOpen(false);
                setMobileSubmenu(null);
              }}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V9.4a8.16 8.16 0 0 0 4.77 1.52V7.47a4.85 4.85 0 0 1-1.01-.78z" />
              </svg>
              <span className="sr-only">TikTok</span>
            </a>
            <a
              href="https://www.linkedin.com/company/gentlemansride"
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                'p-2 rounded-full transition-colors',
                usesDarkChrome
                  ? 'text-white/60 hover:text-white hover:bg-white/10'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent',
              )}
              onClick={() => {
                setMobileMenuOpen(false);
                setMobileSubmenu(null);
              }}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
              <span className="sr-only">LinkedIn</span>
            </a>
          </div>

          {/* App Store / Google Play */}
          <div className="flex items-center justify-center gap-3 mb-5">
            <a
              href="https://apps.apple.com/us/app/distinguished-gentlemans-ride/id6475795204"
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                'inline-flex flex-1 items-center justify-center gap-2 rounded-md border px-3 py-2 transition-colors',
                usesDarkChrome
                  ? 'border-white/20 bg-white/5 text-white hover:bg-white/10'
                  : 'border-border bg-background text-foreground hover:bg-accent',
              )}
              onClick={() => {
                setMobileMenuOpen(false);
                setMobileSubmenu(null);
              }}
              aria-label="Download on the App Store"
            >
              <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
              </svg>
              <div className="leading-tight text-left">
                <div
                  className={cn(
                    'text-[8px] uppercase tracking-wider',
                    usesDarkChrome ? 'text-white/60' : 'text-muted-foreground',
                  )}
                >
                  Download on the
                </div>
                <div className="text-sm font-semibold">App Store</div>
              </div>
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.gentlemansride.dgr"
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                'inline-flex flex-1 items-center justify-center gap-2 rounded-md border px-3 py-2 transition-colors',
                usesDarkChrome
                  ? 'border-white/20 bg-white/5 text-white hover:bg-white/10'
                  : 'border-border bg-background text-foreground hover:bg-accent',
              )}
              onClick={() => {
                setMobileMenuOpen(false);
                setMobileSubmenu(null);
              }}
              aria-label="Get it on Google Play"
            >
              <svg className="h-6 w-6" viewBox="0 0 24 24">
                <path
                  d="M3.4 1.6c-.3.2-.4.6-.4 1v18.8c0 .4.2.7.4.9l10.6-10.6L3.4 1.6z"
                  fill="#00d4ff"
                />
                <path d="M17.7 8.6 4.6 1.2c-.4-.2-.7-.2-1 0l10.6 10.5 3.5-3.1z" fill="#00f176" />
                <path d="m17.7 15.4-3.5-3.1L3.6 22.8c.3.2.6.2 1 0l13.1-7.4z" fill="#ff3a44" />
                <path d="m21 11-3.3-1.9-3.7 2.9 3.7 2.9 3.3-1.9c1-.6 1-2 0-2z" fill="#ffce00" />
              </svg>
              <div className="leading-tight text-left">
                <div
                  className={cn(
                    'text-[8px] uppercase tracking-wider',
                    usesDarkChrome ? 'text-white/60' : 'text-muted-foreground',
                  )}
                >
                  Get it on
                </div>
                <div className="text-sm font-semibold">Google Play</div>
              </div>
            </a>
          </div>

          {/* CTA Buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              asChild
              className={cn(
                'flex-1 h-12',
                usesDarkChrome &&
                  'border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white',
              )}
            >
              <Link
                prefetch={false}
                href={user ? '/my/dashboard' : registerHref}
                onClick={() => {
                  setMobileMenuOpen(false);
                  setMobileSubmenu(null);
                }}
              >
                {user ? t('dashboard') : t('register')}
              </Link>
            </Button>
            <Button
              asChild
              className={cn(
                'flex-1 h-12',
                usesDarkChrome && 'bg-dgr-gold text-stone-950 hover:bg-dgr-gold-light',
              )}
            >
              <Link
                prefetch={false}
                href={donateHref}
                onClick={() => {
                  setMobileMenuOpen(false);
                  setMobileSubmenu(null);
                }}
              >
                {t('donate')}
              </Link>
            </Button>
          </div>
        </div>
      </div>

      {/* Account Menu - Full Screen Overlay (Mobile) */}
      {user && (
        <div
          className={cn(
            'lg:hidden fixed inset-0 z-50 flex flex-col transition-all duration-300 ease-out',
            accountMenuOpen
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-2 pointer-events-none',
            usesDarkChrome
              ? 'bg-stone-950/95 backdrop-blur-md'
              : 'bg-background/95 backdrop-blur-md',
          )}
        >
          {/* Fixed Header with Logo and Close */}
          <div className="flex-shrink-0 h-20 flex items-center justify-between px-6">
            <Link
              prefetch={false}
              href={localePath('/')}
              onClick={() => setAccountMenuOpen(false)}
              className="flex items-center gap-3"
            >
              {logo || (
                <Image
                  src="/images/dgr/DGRLogo.svg"
                  alt={tCommon('brandAlt')}
                  width={140}
                  height={59}
                  className="h-12 w-auto"
                />
              )}
            </Link>
            <Button
              variant="ghost"
              size="icon"
              className={cn(usesDarkChrome && 'text-white hover:bg-white/10')}
              onClick={() => setAccountMenuOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* User Profile Header */}
          <div
            className={cn(
              'px-6 py-5 border-b',
              usesDarkChrome ? 'border-white/10' : 'border-border',
            )}
          >
            <div className="flex items-center gap-4">
              <div
                className={cn(
                  'rounded-full',
                  user.avatarUrl
                    ? cn(
                        'w-[60px] h-[60px] p-[2px] border-2',
                        usesDarkChrome ? 'border-dgr-gold' : 'border-dgr-gold',
                      )
                    : cn(
                        'w-14 h-14 overflow-hidden border-2',
                        usesDarkChrome ? 'border-dgr-gold' : 'border-primary',
                      ),
                )}
              >
                {user.avatarUrl ? (
                  /* eslint-disable-next-line @next/next/no-img-element */
                  <img
                    src={user.avatarUrl}
                    alt={user.name}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  <div
                    className={cn(
                      'w-full h-full flex items-center justify-center text-xl font-medium',
                      'bg-white text-stone-950',
                    )}
                  >
                    {user.name.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <div>
                <p
                  className={cn(
                    'text-lg font-semibold',
                    usesDarkChrome ? 'text-white' : 'text-foreground',
                  )}
                >
                  {user.name}
                </p>
                {user.email && (
                  <p
                    className={cn(
                      'text-sm',
                      usesDarkChrome ? 'text-white/60' : 'text-muted-foreground',
                    )}
                  >
                    {user.email}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Account Menu Content */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <div className="space-y-1">
              {/* Admin Dashboard for tenant admins */}
              {hasAdminDashboardAccess && (
                <Link
                  prefetch={false}
                  href="/dashboard"
                  className={cn(
                    'flex items-center gap-4 py-4 text-lg font-medium border-b transition-colors',
                    usesDarkChrome
                      ? 'text-dgr-gold border-white/10 hover:text-dgr-gold-light'
                      : 'text-primary border-border hover:text-primary/80',
                  )}
                  onClick={() => setAccountMenuOpen(false)}
                >
                  <LayoutDashboard className="w-5 h-5" />
                  <span>{t('adminDashboard')}</span>
                </Link>
              )}

              {/* Event Admin for event admins */}
              {hasEventAdminAccess && (
                <Link
                  prefetch={false}
                  href="/event-admin"
                  className={cn(
                    'flex items-center gap-4 py-4 text-lg font-medium border-b transition-colors',
                    usesDarkChrome
                      ? 'text-dgr-gold border-white/10 hover:text-dgr-gold-light'
                      : 'text-primary border-border hover:text-primary/80',
                  )}
                  onClick={() => setAccountMenuOpen(false)}
                >
                  <LayoutDashboard className="w-5 h-5" />
                  <span>{t('eventAdmin')}</span>
                </Link>
              )}

              <Link
                prefetch={false}
                href="/my/dashboard"
                className={cn(
                  'flex items-center gap-4 py-2 text-lg font-medium border-b transition-colors',
                  usesDarkChrome
                    ? 'text-white border-white/10 hover:text-dgr-gold'
                    : 'text-foreground border-border hover:text-primary',
                )}
                onClick={() => setAccountMenuOpen(false)}
              >
                <User className="w-5 h-5 opacity-70" />
                <span>{t('myDashboard')}</span>
              </Link>
              {entrantUsername && (
                <Link
                  prefetch={false}
                  href={`/fundraiser/${entrantUsername}`}
                  className={cn(
                    'flex items-center gap-4 py-2 text-lg font-medium border-b transition-colors',
                    usesDarkChrome
                      ? 'text-white border-white/10 hover:text-dgr-gold'
                      : 'text-foreground border-border hover:text-primary',
                  )}
                  onClick={() => setAccountMenuOpen(false)}
                >
                  <ExternalLink className="w-5 h-5 opacity-70" />
                  <span>{tMy('viewPublicProfile')}</span>
                </Link>
              )}
              <Link
                prefetch={false}
                href="/my/events"
                className={cn(
                  'flex items-center gap-4 py-2 text-lg font-medium border-b transition-colors',
                  usesDarkChrome
                    ? 'text-white border-white/10 hover:text-dgr-gold'
                    : 'text-foreground border-border hover:text-primary',
                )}
                onClick={() => setAccountMenuOpen(false)}
              >
                <CalendarDays className="w-5 h-5 opacity-70" />
                <span>{t('myEvents')}</span>
              </Link>
              {eventDetail && (
                <Link
                  prefetch={false}
                  href={eventDetail.href}
                  className={cn(
                    'flex items-center gap-4 py-2 text-lg font-medium border-b transition-colors',
                    usesDarkChrome
                      ? 'text-white border-white/10 hover:text-dgr-gold'
                      : 'text-foreground border-border hover:text-primary',
                  )}
                  onClick={() => setAccountMenuOpen(false)}
                >
                  <MapPin className="w-5 h-5 opacity-70" />
                  <span>{t('eventDetails', { eventName: eventDetail.name })}</span>
                </Link>
              )}
              <Link
                prefetch={false}
                href="/my/donations"
                className={cn(
                  'flex items-center gap-4 py-2 text-lg font-medium border-b transition-colors',
                  usesDarkChrome
                    ? 'text-white border-white/10 hover:text-dgr-gold'
                    : 'text-foreground border-border hover:text-primary',
                )}
                onClick={() => setAccountMenuOpen(false)}
              >
                <Heart className="w-5 h-5 opacity-70" />
                <span>{t('myDonations')}</span>
              </Link>
              <Link
                prefetch={false}
                href="/my/orders"
                className={cn(
                  'flex items-center gap-4 py-2 text-lg font-medium border-b transition-colors',
                  usesDarkChrome
                    ? 'text-white border-white/10 hover:text-dgr-gold'
                    : 'text-foreground border-border hover:text-primary',
                )}
                onClick={() => setAccountMenuOpen(false)}
              >
                <Package className="w-5 h-5 opacity-70" />
                <span>{t('myOrders')}</span>
              </Link>
              <Link
                prefetch={false}
                href="/my/downloads"
                className={cn(
                  'flex items-center gap-4 py-2 text-lg font-medium border-b transition-colors',
                  usesDarkChrome
                    ? 'text-white border-white/10 hover:text-dgr-gold'
                    : 'text-foreground border-border hover:text-primary',
                )}
                onClick={() => setAccountMenuOpen(false)}
              >
                <Download className="w-5 h-5 opacity-70" />
                <span>{t('downloads')}</span>
              </Link>
              <Link
                prefetch={false}
                href="/my/offers"
                className={cn(
                  'flex items-center gap-4 py-2 text-lg font-medium border-b transition-colors',
                  usesDarkChrome
                    ? 'text-white border-white/10 hover:text-dgr-gold'
                    : 'text-foreground border-border hover:text-primary',
                )}
                onClick={() => setAccountMenuOpen(false)}
              >
                <Gift className="w-5 h-5 opacity-70" />
                <span>{t('exclusiveOffers')}</span>
              </Link>
              <Link
                prefetch={false}
                href="/my/team/dashboard"
                className={cn(
                  'flex items-center gap-4 py-2 text-lg font-medium border-b transition-colors',
                  usesDarkChrome
                    ? 'text-white border-white/10 hover:text-dgr-gold'
                    : 'text-foreground border-border hover:text-primary',
                )}
                onClick={() => setAccountMenuOpen(false)}
              >
                <Users className="w-5 h-5 opacity-70" />
                <span>{t('myTeam')}</span>
              </Link>
              <Link
                prefetch={false}
                href="/my/settings"
                className={cn(
                  'flex items-center gap-4 py-2 text-lg font-medium border-b transition-colors',
                  usesDarkChrome
                    ? 'text-white border-white/10 hover:text-dgr-gold'
                    : 'text-foreground border-border hover:text-primary',
                )}
                onClick={() => setAccountMenuOpen(false)}
              >
                <Settings className="w-5 h-5 opacity-70" />
                <span>{t('settings')}</span>
              </Link>
            </div>
          </div>

          {/* Fixed Footer - Logout & Social */}
          <div
            className={cn(
              'flex-shrink-0 px-6 py-5 border-t',
              usesDarkChrome ? 'border-white/10' : 'border-border',
            )}
          >
            {/* Social Media Links */}
            <div className="flex items-center justify-center gap-6 mb-5">
              <a
                href="https://instagram.com/gentlemansride"
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  'p-2 rounded-full transition-colors',
                  usesDarkChrome
                    ? 'text-white/60 hover:text-white hover:bg-white/10'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent',
                )}
                onClick={() => setAccountMenuOpen(false)}
              >
                <Instagram className="w-5 h-5" />
                <span className="sr-only">Instagram</span>
              </a>
              <a
                href="https://facebook.com/gentlemansride"
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  'p-2 rounded-full transition-colors',
                  usesDarkChrome
                    ? 'text-white/60 hover:text-white hover:bg-white/10'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent',
                )}
                onClick={() => setAccountMenuOpen(false)}
              >
                <Facebook className="w-5 h-5" />
                <span className="sr-only">Facebook</span>
              </a>
              <a
                href="https://youtube.com/gentlemansride"
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  'p-2 rounded-full transition-colors',
                  usesDarkChrome
                    ? 'text-white/60 hover:text-white hover:bg-white/10'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent',
                )}
                onClick={() => setAccountMenuOpen(false)}
              >
                <Youtube className="w-5 h-5" />
                <span className="sr-only">YouTube</span>
              </a>
              <a
                href="https://x.com/gentlemansride"
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  'p-2 rounded-full transition-colors',
                  usesDarkChrome
                    ? 'text-white/60 hover:text-white hover:bg-white/10'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent',
                )}
                onClick={() => setAccountMenuOpen(false)}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
                <span className="sr-only">X (Twitter)</span>
              </a>
              <a
                href="https://www.tiktok.com/@gentlemansride"
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  'p-2 rounded-full transition-colors',
                  usesDarkChrome
                    ? 'text-white/60 hover:text-white hover:bg-white/10'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent',
                )}
                onClick={() => setAccountMenuOpen(false)}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V9.4a8.16 8.16 0 0 0 4.77 1.52V7.47a4.85 4.85 0 0 1-1.01-.78z" />
                </svg>
                <span className="sr-only">TikTok</span>
              </a>
              <a
                href="https://www.linkedin.com/company/gentlemansride"
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  'p-2 rounded-full transition-colors',
                  usesDarkChrome
                    ? 'text-white/60 hover:text-white hover:bg-white/10'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent',
                )}
                onClick={() => setAccountMenuOpen(false)}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
                <span className="sr-only">LinkedIn</span>
              </a>
            </div>

            {/* Logout Button */}
            <Button
              variant="outline"
              className={cn(
                'w-full h-12',
                usesDarkChrome &&
                  'border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white',
              )}
              onClick={() => {
                setAccountMenuOpen(false);
                void logout();
              }}
              disabled={isLoggingOut}
            >
              <LogOut className="w-4 h-4 mr-2" />
              {isLoggingOut ? t('signingOut') : t('logout')}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
